import { ReactElement, useMemo } from 'react'

import { InvoicesListProvider, InvoicesListProviderProps } from './context/invoicesList'
import { InvoicesListDataProvider } from './context/invoicesListData'
import { InvoicesListFiltersProvider, InvoicesListFiltersProviderProps } from './context/invoicesListFilters'
import { InvoicesTable } from './elements/InvoicesTable'
import { InvoicesTableActions } from './elements/InvoicesTableActions'

type InvoicesListOptions = InvoicesListProviderProps['options']

interface InvoicesListProps {
  columns?: InvoicesListProviderProps['columns']
  contextFilters?: InvoicesListFiltersProviderProps['contextFilters']
  // When `false`, the list mounts but skips data fetching so the table renders its natural empty
  // state. Use for customer-scoped instances whose customerId may not be resolved yet.
  isFetchEnabled?: InvoicesListOptions['isFetchEnabled']
  newDocumentUrl: InvoicesListProviderProps['newDocumentUrl']
  persistFiltersInUrl?: InvoicesListOptions['persistFiltersInUrl']
  // When `true`, the columns filtering sits in the top actions bar next to the period selector
  // instead of the table's bottom bar. Off by default, so other lists keep the bottom placement.
  showColumnsFilteringInActions?: InvoicesListOptions['showColumnsFilteringInActions']
  showPeriodSelector?: InvoicesListOptions['showPeriodSelector']
  showSearch?: InvoicesListOptions['showSearch']
  showTitle?: InvoicesListOptions['showTitle']
  tableId: InvoicesListProviderProps['tableId']
}

export const InvoicesList = ({
  columns,
  contextFilters,
  isFetchEnabled = true,
  newDocumentUrl,
  persistFiltersInUrl = true,
  showColumnsFilteringInActions = false,
  showPeriodSelector = true,
  showSearch = true,
  showTitle = true,
  tableId,
}: InvoicesListProps): ReactElement => {
  const options = useMemo(
    () => ({
      isFetchEnabled,
      persistFiltersInUrl,
      showColumnsFilteringInActions,
      showPeriodSelector,
      showSearch,
      showTitle,
    }),
    [isFetchEnabled, persistFiltersInUrl, showColumnsFilteringInActions, showPeriodSelector, showSearch, showTitle],
  )

  return (
    <InvoicesListProvider columns={columns} newDocumentUrl={newDocumentUrl} options={options} tableId={tableId}>
      <InvoicesListFiltersProvider contextFilters={contextFilters}>
        <InvoicesListDataProvider>
          <InvoicesTableActions />
          <InvoicesTable />
        </InvoicesListDataProvider>
      </InvoicesListFiltersProvider>
    </InvoicesListProvider>
  )
}
